import { useMemo } from 'react';
import { getFormattedDate } from '../../../shared/utils/dateUtils';
import { getHumanizeInbodyType } from '../../../shared/utils/inbodyUtils';
import {
  dateTimeFormat,
  localDateTimeFormat,
} from '../../../shared/constants/apiDateFormat';
import { ScanMetadata } from '../../interfaces/scanMetadata';

export type UseScanRangeOptionsProps = {
  scansMetadata: ScanMetadata[]; // should be sorted by scanDateTime
  startScanId: number | null;
  endScanId: number | null;
};

type ScanDateOption = {
  label: string;
  value: number;
  description: string;
};

export type UseScanRangeOptionsReturn = {
  startScanOptions: ScanDateOption[];
  endScanOptions: ScanDateOption[];
};

export const useScanRangeOptions = ({
  scansMetadata,
  startScanId,
  endScanId,
}: UseScanRangeOptionsProps): UseScanRangeOptionsReturn => {
  const scanOptions = useMemo(() => {
    const scanDateOptions = scansMetadata.map((date) => ({
      label: getFormattedDate(date.scanDateTime, localDateTimeFormat, dateTimeFormat),
      description: getHumanizeInbodyType(date.inbodyType),
      value: date.scanId,
    }));
    return scanDateOptions;
  }, [scansMetadata]);

  const getScanIndex = (scanId: number) =>
    scanOptions.findIndex((scan) => scan.value === scanId);

  const startScanOptions = useMemo(() => {
    if (!endScanId) {
      return scanOptions.slice(1);
    }

    const endScanIndex = getScanIndex(endScanId);

    return scanOptions.slice(endScanIndex + 1);
  }, [scanOptions, endScanId]);

  const endScanOptions = useMemo(() => {
    if (!startScanId) {
      return scanOptions.slice(0, -1);
    }

    const startScanIndex = getScanIndex(startScanId);

    return scanOptions.slice(0, startScanIndex);
  }, [scanOptions, startScanId]);

  return { startScanOptions, endScanOptions };
};
